import React, { useEffect, useState } from "react";
import "./LoanSettlementRequest.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllLoans } from "../redux/slices/loan/loanReportSlice";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileCsv,
  faFileExcel,
  faFilePdf,
  faSearch,
  faFilter,
  faTableList,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { toast } from "react-toastify";

const LoanSettlementRequest = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.userProfile);
  const { loans, loading, error } = useSelector((state) => state.loanReport);

  const [settlementDate, setSettlementDate] = useState(new Date().toISOString().split("T")[0]);
  const [settlementAmount, setSettlementAmount] = useState("");
  const [company, setCompany] = useState(null);
  const [settlementRemark, setSettlementRemark] = useState("");
   const [paymentMode, setPaymentMode] = useState("");
  const [filters, setFilters] = useState({
    loanAccountNumber: "",
    customerName: "",
    customerId: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loanSettlementDialog, setLoanSettlementDialog] = useState({
    open: false,
    loanId: null,
  });

  useEffect(() => {
    dispatch(fetchUserProfile());
    dispatch(fetchAllLoans());
  }, [dispatch]);

  useEffect(() => {
    const fetchCompany = async () => {
      if (!profile?.superAdminId) return;
      try {
        const res = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${profile?.superAdminId}`,
        );
        const data = await res.json();
        if (res.ok && data) {
          if (data.logo)
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, "")}`;
          setCompany(data);
        }
      } catch (err) {
        console.error("Failed to fetch company details", err);
      }
    };
    fetchCompany();
  }, [profile?.superAdminId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(
      e.target.value === "all" ? "all" : parseInt(e.target.value, 10),
    );
    setCurrentPage(1);
  };

  const applyFilters = (loan) => {
    const { loanAccountNumber, customerName, customerId } = filters;
    return (
      (!loanAccountNumber ||
        loan.loanAccountNumber
          ?.toLowerCase()
          .includes(loanAccountNumber.toLowerCase())) &&
      (!customerName ||
        loan.userName?.toLowerCase().includes(customerName.toLowerCase())) &&
      (!customerId ||
        loan.customerId?.toLowerCase().includes(customerId.toLowerCase()))
    );
  };

  // const filteredLoans = Array.isArray(loans) ? loans.filter(applyFilters) : [];
  const filteredLoans = Array.isArray(loans) 
  ? loans.filter(loan => 
      applyFilters(loan) && loan.status?.toLowerCase() === "disbursed"
    ) 
  : [];
  const totalItems = filteredLoans.length;
  const totalPages =
    itemsPerPage === "all" ? 1 : Math.ceil(totalItems / itemsPerPage);
  const startIndex =
    itemsPerPage === "all" ? 0 : (currentPage - 1) * itemsPerPage;
  const paginatedLoans = filteredLoans.slice(
    startIndex,
    itemsPerPage === "all" ? totalItems : startIndex + itemsPerPage,
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const openLoanSettlementDialog = (loanId) =>
    setLoanSettlementDialog({ open: true, loanId });
  const closeLoanSettlementDialog = () => {
    setLoanSettlementDialog({ open: false, loanId: null });
    setSettlementDate("");
    setSettlementRemark("");
    setSettlementAmount("");
    setPaymentMode("");
  };

  const handleLoanSettlement = async (e) => {
    e.preventDefault();
    if (!settlementDate || !settlementAmount ||!paymentMode|| !settlementRemark) {
      toast.warn("All fields are required");
      return;
    }
    try {
     const res= await axios.post(
        `https://payyug.in/api/loan/personal/settlement`,
        // `https://payyug.in/api/loan/personal/settlement${loanCloseDialog.loanId}`,
        {
          loanId:loanSettlementDialog.loanId,
          remark: settlementRemark,
          settlementAmount,
          paymentMode,
        },
      );
      toast.success(res.data.message|| "Settlement request processed successfully");
      dispatch(fetchAllLoans());
      closeLoanSettlementDialog();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Error processing settlement",
      );
    }
  };


  
  // 🔹 Export to Excel

  const exportExcel = () => {
    const data = paginatedLoans.map((loan) => ({
      "Account Number": loan.loanAccountNumber,

      "Customer Id": loan.customerId,

      "Customer Name": loan.userName,

      "Loan Amount": loan.loanAmount,

      "Loan Status": loan.status,
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);

    // 🔹 Header styling

    const headerCells = Object.keys(data[0]);

    headerCells.forEach((_, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });

      worksheet[cellRef].s = {
        font: {
          bold: true,

          color: { rgb: "FFFFFF" },
        },

        fill: {
          fgColor: { rgb: "1F4E78" }, // dark blue
        },

        alignment: {
          horizontal: "center",

          vertical: "center",
        },
      };
    });

    // 🔹 Auto column width

    worksheet["!cols"] = headerCells.map((key) => ({
      wch: Math.max(key.length + 5, 18),
    }));

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Loans");

    XLSX.writeFile(workbook, "Settlement_Report.xlsx");
  };

  // 🔹 Export to CSV

  const exportCSV = () => {
    const data = paginatedLoans.map((loan) => ({
      "Account Number": loan.loanAccountNumber,

      "Customer Id": loan.customerId,

      "Customer Name": loan.userName,

      "Loan Amount": loan.loanAmount,

      "Loan Status": loan.status,
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);

    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);

    const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });

    saveAs(blob, "Settlement_Report.csv");
  };

  // 🔹 Export to PDF

  const exportPDF = async () => {
    const doc = new jsPDF("p", "mm", "a4");

    const pageWidth = doc.internal.pageSize.width;

    const pageHeight = doc.internal.pageSize.height;

    // ================= LOGO =================

    const logoX = 15;

    const logoY = 12;

    const logoSize = 25;

    const logoPadding = 2;

    // Border color & thickness

    doc.setDrawColor(0);

    doc.setLineWidth(0.2);

    // Calculate center of logo

    const centerX = logoX + logoSize / 2;

    const centerY = logoY + logoSize / 2;

    // Radius = half of logo + padding

    const radius = logoSize / 2 + logoPadding;

    // Draw circle

    doc.circle(centerX, centerY, radius);

    // Logo image

    if (company?.logoUrl) {
      try {
        const logoResp = await fetch(company.logoUrl);

        const logoBlob = await logoResp.blob();

        const reader = new FileReader();

        await new Promise((resolve) => {
          reader.onload = () => {
            doc.addImage(
              reader.result,
              "PNG",
              logoX,
              logoY,
              logoSize,
              logoSize,
            );

            resolve();
          };

          reader.readAsDataURL(logoBlob);
        });
      } catch {
        // silent fail
      }
    }

    // ================= COMPANY NAME (AUTO WRAP) =================

    doc.setFont("helvetica", "bold");

    doc.setFontSize(14);

    const nameX = logoX + logoSize + 5;

    const nameY = 18;

    const nameMaxWidth = pageWidth - nameX - 15;

    const companyNameText = doc.splitTextToSize(
      (company?.name || "COMPANY NAME").toUpperCase(),

      nameMaxWidth,
    );

    doc.text(companyNameText, nameX, nameY);

    // Calculate Y after company name

    let currentY = nameY + companyNameText.length * 6;

    // ================= COMPANY DETAILS =================

    doc.setFont("helvetica", "normal");

    doc.setFontSize(11);

    const detailX = nameX;

    const detailMaxWidth = nameMaxWidth;

    // Address

    const addressText = doc.splitTextToSize(
      company?.address || "Company Address",

      detailMaxWidth,
    );

    doc.text(addressText, detailX, currentY);

    currentY += addressText.length * 5;

    // Email

    const emailText = doc.splitTextToSize(
      `Email: ${company?.email || "-"}`,

      detailMaxWidth,
    );

    doc.text(emailText, detailX, currentY);

    currentY += emailText.length * 5;

    // Phone

    const phoneText = doc.splitTextToSize(
      `Phone: ${company?.contact || "-"}`,

      detailMaxWidth,
    );

    doc.text(phoneText, detailX, currentY);

    currentY += phoneText.length * 5;

    // Divider

    doc.line(10, currentY + 4, pageWidth - 10, currentY + 4);

    // ================= REPORT TITLE =================

    doc.setFont("helvetica", "bold");

    doc.setFontSize(13);

    doc.text(
      "Loan Closer Request Report",

      pageWidth / 2,

      currentY + 14,

      { align: "center" },
    );

    const tableColumn = [
      "SN",

      "Loan Account",

      "Customer Id",

      "Customer Name",

      "Loan Amount",

      "Loan Status",
    ];

    const tableRows = [];

    paginatedLoans.forEach((loan, index) => {
      const loanData = [
        index + 1,

        loan.loanAccountNumber,

        loan.customerId,

        loan.userName,

        loan.loanAmount,

        loan.status,
      ];

      tableRows.push(loanData);
    });

    autoTable(doc, {
      head: [tableColumn],

      body: tableRows,

      startY: currentY + 20,

      theme: "grid",

      styles: {
        fontSize: 7,

        cellPadding: 3,

        halign: "center",
      },

      headStyles: {
        fontStyle: "bold",

        fontSize: 7,
      },

      footStyles: {
        fillColor: [220, 220, 220],

        textColor: 20,

        fontStyle: "bold",

        fontSize: 8,
      },

      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
    });

    doc.save("Settlement_Report.pdf");
  };

  return (
    <div className="lsr-app-container">
      {/* Search Filter Section */}
      <section className="lsr-app-filter-box">
        <div className="lsr-app-card-title">
          <FontAwesomeIcon icon={faFilter} className="lsr-app-icon-blue" />
          <h3>Settlement Request Filter</h3>
        </div>
        <div className="lsr-app-filter-grid">
          <div className="lsr-app-field">
            <label>Loan Account Number</label>
            <div className="lsr-app-input-with-icon">
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="text"
                name="loanAccountNumber"
                value={filters.loanAccountNumber}
                onChange={handleInputChange}
                placeholder="Search account..."
              />
            </div>
          </div>
          <div className="lsr-app-field">
            <label>Customer Name</label>
            <input
              type="text"
              name="customerName"
              value={filters.customerName}
              onChange={handleInputChange}
              placeholder="Search name..."
            />
          </div>
          <div className="lsr-app-field">
            <label>Customer ID</label>
            <input
              type="text"
              name="customerId"
              value={filters.customerId}
              onChange={handleInputChange}
              placeholder="Search ID..."
            />
          </div>
        </div>
      </section>

      {/* Table Data Section */}
      <section className="lsr-app-data-box">
        <div className="lsr-app-data-header">
          <div className="lsr-app-header-left">
            <FontAwesomeIcon icon={faTableList} className="lsr-app-icon-blue" />
            <h3>Loan Records</h3>
            <select
              className="lsr-app-select-limit"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <option value={10}>10 Rows</option>
              <option value={25}>25 Rows</option>
              <option value={50}>50 Rows</option>
              <option value="all">All Records</option>
            </select>
          </div>
          <div className="lsr-app-export-btns">
            <button className="lsr-app-btn-export csv" onClick={exportCSV}>
              <FontAwesomeIcon icon={faFileCsv} /> CSV
            </button>
            <button className="lsr-app-btn-export excel" onClick={exportExcel}>
              <FontAwesomeIcon icon={faFileExcel} /> Excel
            </button>
            <button className="lsr-app-btn-export pdf" onClick={exportPDF}>
              <FontAwesomeIcon icon={faFilePdf} /> PDF
            </button>
          </div>
        </div>

        <div className="lsr-app-table-container">
          <table className="lsr-app-table">
            <thead>
              <tr>
                <th>SN</th>
                <th>Account Number</th>
                <th>Customer ID</th>
                <th>Customer Name</th>
                <th>Loan Amount</th>
                <th>Status</th>
                <th style={{ textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="lsr-app-text-center">
                    Loading data...
                  </td>
                </tr>
              ) : paginatedLoans.length > 0 ? (
                paginatedLoans.map((loan, index) => {
                  const status = loan.status?.toLowerCase() || "";
                  return (
                    <tr
                      key={loan.id || index}
                      className={`lsr-app-row-${status}`}
                    >
                      <td>{startIndex + index + 1}</td>
                      <td className="lsr-app-bold-text">
                        {loan.loanAccountNumber || "N/A"}
                      </td>
                      <td>{loan.customerId || "N/A"}</td>
                      <td>{loan.userName || "N/A"}</td>
                      <td className="lsr-app-price">
                        ₹{Number(loan.loanAmount || 0).toLocaleString("en-IN")}
                      </td>
                      <td>
                        <span
                          className={`lsr-app-badge lsr-app-badge-${status}`}
                        >
                          {loan.status}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        
                          <button
                            className="lsr-app-btn-action"
                            onClick={() => openLoanSettlementDialog(loan.id)}
                          >
                            Settlement
                          </button>
                       
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="lsr-app-text-center">
                    No loans found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {itemsPerPage !== "all" && totalPages > 1 && (
          <div className="lsr-app-pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span>
              Page <strong>{currentPage}</strong> of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </section>

      {/* Settlement Modal */}
      {loanSettlementDialog.open && (
        <div className="lsr-app-modal-overlay">
          <div className="lsr-app-modal-content">
            <div className="lsr-app-modal-header">
              <h3>Confirm Settlement</h3>
              <p>Enter closure details to proceed</p>
            </div>
            <form onSubmit={handleLoanSettlement}>
              <div className="lsr-app-modal-body">
                <div className="lsr-app-field">
                  <label>Settlement Date</label>
                  <input
                    type="date"
                    value={settlementDate}
                    onChange={(e) => setSettlementDate(e.target.value)}
                    required
                  />
                </div>
                <div className="lsr-app-field">
                  <label>Settlement Amount</label>
                  <input
                    type="text"
                    value={settlementAmount}
                    onChange={(e) => setSettlementAmount(e.target.value)}
                    required
                  />
                </div>
               <div className="lsr-app-field">
  <label>Payment Mode</label>
  <select
    id="paymentMode"
    value={paymentMode}
    onChange={(e) => setPaymentMode(e.target.value)}
    required
  >
    <option value="">Select Mode</option>
    <option value="Cash">Cash</option>
    <option value="UPI">UPI</option>
    <option value="Net Banking">Net Banking</option>
    <option value="Card">Debit/Credit Card</option>
  </select>
</div>
                <div className="lsr-app-field">
                  <label>Remarks</label>
                  <textarea
                    rows="3"
                    placeholder="Enter closure reason..."
                    value={settlementRemark}
                    onChange={(e) => setSettlementRemark(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="lsr-app-modal-actions">
                <button
                  type="button"
                  className="lsr-app-btn-secondary"
                  onClick={closeLoanSettlementDialog}
                >
                  Cancel
                </button>
                <button type="submit" className="lsr-app-btn-primary">
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanSettlementRequest;
